/**
 * Step 3a sample-email preview card (`review_email_preview` frontend tool).
 *
 * The run is parked while this card is up; the operator answers with exactly one
 * of approve / change_style / change_template / revise. The three adjustments open
 * a modal first — opening, cancelling or ✕-ing a modal answers nothing, only the
 * modal's submit button does. Mirrors the ABM preview in the product demo
 * (`campaign-copilot-demo-v2.html`, `abmStep3` + edit/tone/template modals).
 *
 * Plain presentational component, like the panels in HumanInterruptPanel.tsx:
 * AgUiChatPanel owns `respond` and passes `onAction`.
 *
 * Style keys are fixed here and mirrored in the SOP
 * (`domains/cmhk-campaign-orchestration/docs/steps/step3-personalization.md`),
 * which maps each key to a `refine_plan` instruction. Change both together.
 * Templates are dynamic — the agent passes them from `list_registered_templates()`.
 */
import { useState } from 'react';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Badge from '@amzn/awsui-components-react/polaris/badge';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-react/polaris/status-indicator';
import Textarea from '@amzn/awsui-components-react/polaris/textarea';
import Tiles from '@amzn/awsui-components-react/polaris/tiles';
import { safeHref } from './HumanInterruptPanel';
import { WorkspaceFileLink } from './WorkspaceFileLink';

export const STYLE_OPTIONS = [
  { key: 'professional', label: '🏢 专业正式', hint: '默认，通用 B2B' },
  { key: 'executive_brief', label: '💼 Executive Brief', hint: 'C-suite，简洁精炼' },
  { key: 'friendly', label: '🤝 亲和轻松', hint: '已有关系的客户' },
  { key: 'data_driven', label: '📊 数据驱动', hint: '技术决策者' },
  { key: 'action_oriented', label: '🚀 行动导向', hint: '紧急推动，强 CTA' },
  { key: 'educational', label: '🎓 科普教育', hint: '知识传播，解释性强' },
] as const;

const DEFAULT_STYLE = 'professional';

export interface PreviewTemplate {
  template_id: string;
  template_name?: string;
  content_type?: string;
}

export type PreviewAction =
  | { action: 'approve' }
  | { action: 'change_style'; style: string; extra?: string }
  | { action: 'change_template'; template_id: string }
  | { action: 'revise'; feedback: string };

export interface PreviewPanelData {
  subject?: string;
  portalUrl?: string;
  sampleRecipient?: string;
  currentTemplateId?: string;
  templates: PreviewTemplate[];
  currentStyle: string;
  contentPlanId?: string;
  planVersion?: number;
  memberCount?: number;
  ctaUrl?: string;
  ebrStatus?: string;
}

export interface PreviewReviewPanelProps extends PreviewPanelData {
  /** Whether the run is still waiting on this card — every button disables when false. */
  active: boolean;
  onAction: (a: PreviewAction) => void;
}

/** Map the tool call's snake_case arguments to panel props. */
export function previewPanelPropsFromArgs(args: Record<string, unknown> | undefined): PreviewPanelData {
  const a = args ?? {};
  const str = (k: string) => (typeof a[k] === 'string' && a[k] ? (a[k] as string) : undefined);
  const num = (k: string) => (typeof a[k] === 'number' ? (a[k] as number) : undefined);
  const templates = Array.isArray(a.templates)
    ? (a.templates as unknown[]).filter(
        (t): t is PreviewTemplate =>
          !!t && typeof t === 'object' && typeof (t as PreviewTemplate).template_id === 'string',
      )
    : [];
  return {
    subject: str('subject'),
    portalUrl: str('portal_url'),
    sampleRecipient: str('sample_recipient'),
    currentTemplateId: str('current_template_id'),
    templates,
    currentStyle: str('current_style') ?? DEFAULT_STYLE,
    contentPlanId: str('content_plan_id'),
    planVersion: num('plan_version'),
    memberCount: num('member_count'),
    ctaUrl: str('cta_url'),
    ebrStatus: str('ebr_status'),
  };
}

export function styleLabel(key: string): string {
  return STYLE_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

export function templateLabel(data: PreviewPanelData, id: string | undefined): string {
  if (!id) return '—';
  return data.templates.find((t) => t.template_id === id)?.template_name ?? id;
}

/** Live card: EBR as a Polaris StatusIndicator, so its colour follows the theme. */
const EBR_STATUS: Record<string, { type: StatusIndicatorProps.Type; text: string }> = {
  pass: { type: 'success', text: 'Pass' },
  pass_with_warning: { type: 'warning', text: 'Pass with Warning' },
  hold: { type: 'error', text: 'Hold' },
  error: { type: 'error', text: 'Hold' },
  pending: { type: 'pending', text: 'Pending' },
};

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

/**
 * Recipient / Subject / chips / CTA — the body shared by the live card and the
 * settled audit card, so a settled preview reads the same as the one it answered.
 */
function PreviewSummary({ data }: { data: PreviewPanelData }) {
  const { subject, portalUrl, sampleRecipient, currentTemplateId, currentStyle, planVersion, memberCount, ctaUrl, ebrStatus } = data;
  const href = safeHref(portalUrl);
  const chips = [`模板: ${templateLabel(data, currentTemplateId)}`, `风格: ${styleLabel(currentStyle)}`];
  if (typeof memberCount === 'number') chips.push(`目标 ${memberCount.toLocaleString()} 人`);
  const ebr = ebrStatus ? EBR_STATUS[ebrStatus] : undefined;
  return (
    <>
      <div className="preview-card__header">
        <p className="hitl-interrupt-card__message">
          <strong>邮件预览 · 示例收件人: {sampleRecipient ?? '—'}</strong>
        </p>
        {typeof planVersion === 'number' && <Badge color="blue">Plan v{planVersion}</Badge>}
      </div>

      {(subject || href) && (
        <div className="preview-card__email">
          {subject && (
            <div className="preview-card__subject">
              <span className="preview-card__subject-label">Subject</span>
              <span className="preview-card__subject-text">{subject}</span>
            </div>
          )}
          {href && (
            <WorkspaceFileLink className="preview-card__open" href={href}>
              📧 打开完整预览
            </WorkspaceFileLink>
          )}
        </div>
      )}

      <div className="preview-card__chips">
        {chips.map((c) => <span key={c} className="preview-card__chip">{c}</span>)}
        {ebrStatus && (
          <span className="preview-card__ebr">
            {ebr
              ? <StatusIndicator type={ebr.type}>EBR {ebr.text}</StatusIndicator>
              : `EBR ${ebrStatus}`}
          </span>
        )}
      </div>
      {ctaUrl && <p className="preview-card__cta" title={ctaUrl}>CTA: {ctaUrl}</p>}
    </>
  );
}

/** Settled-card pill for a `review_email_preview` result string. */
export function previewOutcome(
  result: string | undefined,
  d: PreviewPanelData,
): { verdict: 'approved' | 'rejected' | 'choice' | 'unknown'; text: string } {
  let r: Record<string, unknown> = {};
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : undefined;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) r = parsed;
  } catch { /* non-JSON result — falls through to unknown */ }
  switch (r.action) {
    case 'approve':
      return { verdict: 'approved', text: '已采用此预览' };
    case 'change_style': {
      const extra = typeof r.extra === 'string' && r.extra ? ` · ${truncate(r.extra, 40)}` : '';
      return { verdict: 'choice', text: `🔄 已切换风格 → ${styleLabel(String(r.style ?? ''))}${extra}` };
    }
    case 'change_template':
      return {
        verdict: 'choice',
        text: `🎨 已换模板 → ${templateLabel(d, typeof r.template_id === 'string' ? r.template_id : undefined)}（之前的修改已丢弃）`,
      };
    case 'revise':
      return { verdict: 'choice', text: `✏️ 已提交修改：${truncate(String(r.feedback ?? ''), 80)}` };
    // Typed in the composer instead of clicking (see TYPEABLE_GATE_KINDS).
    case 'typed':
      return { verdict: 'choice', text: `💬 已回复：${truncate(String(r.text ?? ''), 80)}` };
    case 'abort':
      return { verdict: 'rejected', text: '已中止' };
    default:
      return { verdict: 'unknown', text: '已处理' };
  }
}

type OpenModal = 'style' | 'template' | 'revise' | null;

export function PreviewReviewPanel(props: PreviewReviewPanelProps) {
  const { subject, currentTemplateId, templates, currentStyle, active, onAction } = props;
  const [openState, setOpen] = useState<OpenModal>(null);
  const [style, setStyle] = useState<string>(currentStyle);
  const [extra, setExtra] = useState('');
  const [templateId, setTemplateId] = useState<string | undefined>(currentTemplateId);
  const [feedback, setFeedback] = useState('');
  // A settled or inactive card never keeps a modal on screen.
  const open: OpenModal = active ? openState : null;
  const close = () => setOpen(null);

  const incomplete = !subject || !currentTemplateId;
  const canSwitchTemplate = !incomplete && templates.length > 1;
  const extraTrimmed = extra.trim();
  const feedbackTrimmed = feedback.trim();

  const openStyle = () => { setStyle(currentStyle); setExtra(''); setOpen('style'); };
  const openTemplate = () => { setTemplateId(currentTemplateId); setOpen('template'); };
  const openRevise = () => { setFeedback(''); setOpen('revise'); };

  const footer = (submitLabel: string, disabled: boolean, onSubmit: () => void) => (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={close}>取消</Button>
        <Button variant="primary" disabled={disabled} onClick={() => { close(); onSubmit(); }}>{submitLabel}</Button>
      </SpaceBetween>
    </Box>
  );

  return (
    <div className="hitl-interrupt-card">
      <div className="hitl-interrupt-card__icon">📨</div>
      <div className="hitl-interrupt-card__body">
        <PreviewSummary data={props} />

        {incomplete && (
          <Alert type="warning">预览参数不完整（缺 Subject 或模板），只能确认或用文字修改。</Alert>
        )}
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" disabled={!active} onClick={() => onAction({ action: 'approve' })}>
            确认，开始批量生成
          </Button>
          <Button disabled={!active} onClick={openRevise}>修改内容</Button>
          {!incomplete && <Button disabled={!active} onClick={openStyle}>换风格</Button>}
          {canSwitchTemplate && <Button disabled={!active} onClick={openTemplate}>换模板</Button>}
        </SpaceBetween>
      </div>

      {open === 'style' && (
      <Modal
        visible
        onDismiss={close}
        header="🔄 选择邮件风格"
        footer={footer('重新生成', style === currentStyle && !extraTrimmed, () =>
          onAction(extraTrimmed
            ? { action: 'change_style', style, extra: extraTrimmed }
            : { action: 'change_style', style }))}
      >
        <SpaceBetween size="m">
          <Box variant="small">单选一种风格，确认后将重新生成邮件</Box>
          <Tiles
            value={style}
            onChange={({ detail }) => setStyle(detail.value)}
            columns={3}
            items={STYLE_OPTIONS.map((o) => ({
              value: o.key,
              label: o.key === currentStyle ? `${o.label}（当前）` : o.label,
              description: o.hint,
            }))}
          />
          <FormField label="额外指令 (可选)">
            <Input
              value={extra}
              onChange={({ detail }) => setExtra(detail.value)}
              placeholder="例如: 语气更紧迫一些、强调成本优化"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
      )}

      {open === 'template' && (
      <Modal
        visible
        onDismiss={close}
        header="🎨 选择邮件模板"
        footer={footer('应用模板', !templateId || templateId === currentTemplateId, () =>
          templateId && onAction({ action: 'change_template', template_id: templateId }))}
      >
        <SpaceBetween size="m">
          <Tiles
            value={templateId ?? null}
            onChange={({ detail }) => setTemplateId(detail.value)}
            columns={2}
            items={templates.map((t) => ({
              value: t.template_id,
              label: t.template_name ?? t.template_id,
              description: t.template_id === currentTemplateId
                ? `当前选择${t.content_type ? ` · ${t.content_type}` : ''}`
                : t.content_type,
            }))}
          />
          <Box variant="small">⚠️ 换模板会重新生成 plan，之前的修改不会保留</Box>
        </SpaceBetween>
      </Modal>
      )}

      {open === 'revise' && (
      <Modal
        visible
        onDismiss={close}
        header="✏️ 修改邮件内容"
        footer={footer('提交修改', !feedbackTrimmed, () =>
          onAction({ action: 'revise', feedback: feedbackTrimmed }))}
      >
        <FormField label="用自然语言描述修改（将应用到全部收件人）">
          <Textarea
            value={feedback}
            onChange={({ detail }) => setFeedback(detail.value)}
            placeholder="例如: 第二段改简洁一点，加上 migration use case"
          />
        </FormField>
      </Modal>
      )}
    </div>
  );
}

/**
 * Audit card left in the transcript once the preview gate is answered: the same
 * summary as the live card, no buttons, plus the outcome line. Icon and muted
 * shell follow SettledApprovalCard.
 */
export function SettledPreviewCard({ data, result }: { data: PreviewPanelData; result: string | undefined }) {
  const outcome = previewOutcome(result, data);
  const icon = outcome.verdict === 'approved' ? '✅' : outcome.verdict === 'rejected' ? '❌' : '⚪';
  return (
    <div className="hitl-interrupt-card hitl-interrupt-card--settled">
      <div className="hitl-interrupt-card__icon">{icon}</div>
      <div className="hitl-interrupt-card__body">
        <PreviewSummary data={data} />
        <p className="hitl-interrupt-card__prompt hitl-interrupt-card__prompt--settled">{outcome.text}</p>
      </div>
    </div>
  );
}
